import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SuggestionJob } from '@nemo/api';
import { useEffect, useState } from 'react';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';

export type SuggestStage = 'pre_seed' | 'seed' | 'series_a' | 'series_b' | 'series_c_plus';
/** Who a run may suggest: the Tracked list, what we do not track yet, or both. */
export type SuggestScope = 'tracked' | 'discover' | 'both';

export interface SuggestRequest {
  portfolioCompanyId: string | null;
  description: string;
  stage: SuggestStage | null;
  scope: SuggestScope;
  includeCorporates: boolean;
}

export interface Suggestion {
  /** The run being worked on or last asked for. */
  readonly current: SuggestionJob | null;
  /** What to show: the newest finished answer, kept while a re-run works. */
  readonly shown: SuggestionJob | null;
  readonly running: boolean;
  readonly isStarting: boolean;
  start: (request: SuggestRequest) => void;
}

const POLL_MS = 2_000;

/**
 * One drawer's suggestion run: the latest one for this list (or company), a
 * poll while it works, and the answer it lands on. A re-run keeps the last
 * answer on screen until the new one is done.
 */
export function useSuggestion(scope: {
  listId: string | null;
  companyId: string | null;
}): Suggestion {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const toast = useToast();
  const latestInput =
    scope.listId !== null
      ? { listId: scope.listId }
      : scope.companyId !== null
        ? { portfolioCompanyId: scope.companyId }
        : null;
  const latest = useQuery(
    trpc.suggest.latestFor.queryOptions(latestInput ?? { listId: '' }, {
      enabled: latestInput !== null,
    }),
  );
  const [startedId, setStartedId] = useState<string | null>(null);
  const currentId = startedId ?? latest.data?.current?.jobId ?? null;

  const status = useQuery(
    trpc.suggest.status.queryOptions(
      { jobId: currentId ?? '', listId: scope.listId },
      {
        enabled: currentId !== null,
        refetchInterval: (query) => {
          const state = query.state.data?.status;
          return state === 'queued' || state === 'running' ? POLL_MS : false;
        },
      },
    ),
  );

  const current = status.data ?? latest.data?.current ?? null;
  const running = current?.status === 'queued' || current?.status === 'running';
  const shown = current?.status === 'done' ? current : (latest.data?.lastDone ?? null);

  // When the run lands, the "latest" answer is this one now.
  const landed = current?.status === 'done' || current?.status === 'failed';
  const landedId = landed ? (current?.jobId ?? null) : null;
  useEffect(() => {
    if (landedId === null) return;
    void queryClient.invalidateQueries({ queryKey: trpc.suggest.latestFor.queryKey() });
  }, [landedId, queryClient, trpc]);

  const start = useMutation(
    trpc.suggest.start.mutationOptions({
      onSuccess: ({ jobId }) => setStartedId(jobId),
      onError: (error) => toast(error.message),
    }),
  );

  return {
    current,
    shown,
    running,
    isStarting: start.isPending,
    start: (request) =>
      start.mutate({
        ...(request.portfolioCompanyId === null
          ? {}
          : { portfolioCompanyId: request.portfolioCompanyId }),
        ...(request.description.trim() === '' ? {} : { description: request.description.trim() }),
        ...(request.stage === null ? {} : { stage: request.stage }),
        scope: request.scope,
        ...(request.scope === 'tracked' ? {} : { includeCorporates: request.includeCorporates }),
        ...(scope.listId === null ? {} : { listId: scope.listId }),
      }),
  };
}
